import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { StackedListComponent, StackedListItem } from '@tcc-mono/shared/ui/stacked-list';
import {
  AnimalSelection,
  RvoAnimal,
  SelectionMarkColor,
  animalSelectionUsesRvoPool,
  getMarkColorFilterTitle,
  getSelectionMarkColorHex,
  isAfterBirthDateRange,
  isBeforeBirthDateRange,
  matchesBirthDateRange,
  suggestMarkColorByBirthDate,
} from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { AnimalSelectionAddAnimal, AnimalSelectionService, StableService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { BehaviorSubject, Observable, Subscription, catchError, combineLatest, filter, finalize, from, map, of, switchMap, tap, take } from 'rxjs';
import { AnimalSelectionNewAnimalDialogComponent } from '@tcc-mono/vandijk/shared/features/animal-selections/feature/dialogs/animal-selection-new-animal-dialog';
import { CalcAgePipe } from '@tcc-mono/shared/utils';
import dayjs from 'dayjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastService } from '@tcc-mono/shared/ui/services';
import { buildAnimalSelectionKeySet, rvoAnimalKey } from './animal-selection-animal-keys.util';
import { EnrichedRvoAnimal } from './animal-selection-animal-dialog.types';
import { KspRvoPoolCacheService } from './ksp-rvo-pool-cache.service';

export type { EnrichedRvoAnimal };

/** Minimale selectie-context voor de pool-dialog (geen volledige animals-array). */
export type AnimalSelectionPoolContext = Pick<
  AnimalSelection,
  'id' | 'source_stable_id' | 'min_birth_date' | 'max_birth_date' | 'source' | 'use_rvo' | 'species'
>;

@UntilDestroy()
@Component({
  templateUrl: './animal-selection-animal-dialog.component.html',
  styleUrls: ['./animal-selection-animal-dialog.component.scss']
})
export class AnimalSelectionAnimalDialogComponent implements OnInit {
  private readonly _stableService: StableService = inject(StableService);
  private readonly _animalSelectionService: AnimalSelectionService = inject(AnimalSelectionService);
  private readonly _modalCtrl: ModalController = inject(ModalController);
  private readonly _poolCache: KspRvoPoolCacheService = inject(KspRvoPoolCacheService);
  private readonly _agePipe: CalcAgePipe = inject(CalcAgePipe);
  private readonly _alertCtrl: AlertController = inject(AlertController);
  private readonly _toast: ToastService = inject(ToastService);
  private readonly _repo: AnimalSelectionRepository = inject(AnimalSelectionRepository);

  private _selectionColors: SelectionMarkColor[] = [];
  public usedColors: SelectionMarkColor[] = [];
  private readonly _selectionColors$ = new BehaviorSubject<SelectionMarkColor[]>([]);

  readonly filterByColorValue$ = new BehaviorSubject<string | null>(null);
  private readonly _filterByColorValue$ = this.filterByColorValue$;

  @Input() public stableId: number;
  /** Keys (country_code+identification_number) van dieren al op de selectie — O(1) exclude. */
  @Input()
  public set animalsOnSelectionKeys(keys: Set<string> | string[] | undefined | null) {
    if (keys instanceof Set) {
      this._excludeKeys = keys;
    } else if (Array.isArray(keys)) {
      this._excludeKeys = new Set(keys);
    } else {
      this._excludeKeys = new Set();
    }
  }

  private _excludeKeys = new Set<string>();
  @Input() public selection: AnimalSelectionPoolContext;
  @Input() public scannedIdentificationNumber: string;

  private readonly _rvoAnimals$ = new BehaviorSubject<RvoAnimal[]>([]);
  private readonly _enrichedAnimals$ = new BehaviorSubject<EnrichedRvoAnimal[]>([]);

  public animalsForCounts$: Observable<EnrichedRvoAnimal[]>;
  public toolbarData$: Observable<{ animals: EnrichedRvoAnimal[]; colors: SelectionMarkColor[]; countsByColor: Record<string, number> }>;
  public filteredAnimals$: Observable<EnrichedRvoAnimal[]>;

  public listItem: StackedListItem = {
    fields: [
      { property: (animal: RvoAnimal) => `${animal.dierLandcode}${animal.dierLevensnummer}` },
      { property: (animal: RvoAnimal) => `Werknummer: ${animal.dierWerknummer}` },
      { property: (animal: RvoAnimal) => `Kleur: ${animal.dierHaarkleur}` },
      {
        property: (animal: RvoAnimal) => `Leeftijd: ${this._ageOf(animal)} dgn`,
        color: (animal: RvoAnimal) =>
          this._isBirthDateInSelectionRange(animal.geboorteDatum) ? 'dark' : 'danger',
      }
    ],
    icons: [
      {
        icon: (animal: RvoAnimal) =>
          (animal.dierGeslacht?.toLocaleLowerCase() === 'm' ? 'mars' : animal.dierGeslacht?.toLocaleLowerCase() === 'v' ? 'venus' : null),
        color: (animal: RvoAnimal) =>
          (animal.dierGeslacht?.toLocaleLowerCase() === 'm' ? 'male' : animal.dierGeslacht?.toLocaleLowerCase() === 'v' ? 'female' : null)
      }
    ]
  };

  private readonly _q = new BehaviorSubject<string>('');
  private readonly _q$ = this._q.asObservable();

  /** Wacht op API (geen cache). */
  public loading = true;
  /** Chunked verwerking bezig — lijst vult zich progressief. */
  public processing = false;
  public readonly skeletonRows = [0, 1, 2, 3, 4, 5, 6, 7];

  /** Hoogte per RVO-pool item (cdk-virtual-scroll). */
  public readonly scrollItemSize = 110;

  @ViewChild('stackedList') stackedList: StackedListComponent;

  private _enrichSubscription?: Subscription;

  public trackByAnimal = (_index: number, item: RvoAnimal) =>
    item?.dierLevensnummer != null ? `${item.dierLandcode ?? ''}_${item.dierLevensnummer}` : _index;

  private _storageKey(): string {
    return !animalSelectionUsesRvoPool(this.selection ?? {})
      ? 'KSP-animals-selection-' + this.selection.id
      : 'KSP-animals-' + this.stableId;
  }

  public ngOnInit(): void {
    this.listItem.rowMarkColor = (item: RvoAnimal) => (item as EnrichedRvoAnimal).suggestedColorValue ?? null;
    this._initObservables();

    // Modal meteen tonen (skeleton) — zware init pas na eerste paint
    requestAnimationFrame(() => this._initData());
  }

  private _initObservables(): void {
    this.animalsForCounts$ = combineLatest([this._enrichedAnimals$, this._q$]).pipe(
      map(([animals, q]) => this._applySearchFilter(animals, q))
    );

    this.toolbarData$ = combineLatest([this.animalsForCounts$, this._selectionColors$]).pipe(
      map(([animals, colors]) => ({
        animals,
        colors,
        countsByColor: this._countsByColorFromEnriched(animals, colors)
      }))
    );

    this.filteredAnimals$ = combineLatest([
      this._enrichedAnimals$,
      this._q$,
      this._filterByColorValue$,
      this._selectionColors$
    ]).pipe(
      map(([animals, q, colorValue, selectionColors]) =>
        this._applyListFilters(animals, q, colorValue, selectionColors)
      )
    );
  }

  private _initData(): void {
    this._loadExcludeKeysFromRepo();

    const storageKey = this._storageKey();
    const cached = this._poolCache.getParsedAnimals(storageKey);
    if (cached.length) {
      this.loading = false;
      this._rvoAnimals$.next(cached);

      const enrichedCached = this._poolCache.getEnrichedList(storageKey);
      if (enrichedCached) {
        this._enrichedAnimals$.next(this._excludeOnSelection(enrichedCached));
      } else {
        this._startEnrichPipeline(cached, storageKey);
      }
    }

    if (this.selection?.id) {
      this._repo.getSelectionMarkColorsForSelection(this.selection.id).pipe(take(1)).subscribe((colors) => {
        this._selectionColors = (colors || []).filter((c) => c.used !== false);
        this.usedColors = this._selectionColors;
        this._selectionColors$.next(this._selectionColors);

        const key = this._storageKey();
        const reapplied = this._poolCache.reapplyColors(key, this._selectionColors, (dob, cols) =>
          suggestMarkColorByBirthDate(dob, cols)
        );
        if (reapplied) {
          this._enrichedAnimals$.next(this._excludeOnSelection(reapplied));
        } else {
          const animals = this._rvoAnimals$.getValue();
          if (animals.length && !this._poolCache.getEnrichedList(key)) {
            this._startEnrichPipeline(animals, key);
          }
        }
      });
    }

    this._getAnimals().pipe(untilDestroyed(this)).subscribe();

    if (this.scannedIdentificationNumber) {
      this._setupScannedAnimalHandler();
    }
  }

  private _loadExcludeKeysFromRepo(): void {
    if (this._excludeKeys.size) {
      return;
    }
    const selection = this._repo.getActiveEntity();
    if (selection?.animals?.length) {
      this._excludeKeys = buildAnimalSelectionKeySet(selection.animals);
    }
  }

  private _setupScannedAnimalHandler(): void {
    this._rvoAnimals$
      .pipe(
        filter((animals) => animals.length > 0),
        take(1),
        map((animals) => animals.find((animal) => animal.dierLevensnummer === this.scannedIdentificationNumber)),
        tap((animal: RvoAnimal | undefined) => {
          if (animal) {
            this.selectAnimal(animal);
          } else {
            this._toast.show({
              header: 'Dier niet gevonden',
              message: 'Het dier met identificatie nummer ' + this.scannedIdentificationNumber + ' staat er niet tussen...',
              color: 'danger'
            });
          }
        })
      )
      .subscribe();
  }

  /** Backwards compat: bouw exclude-set uit meegegeven animals-array. */
  @Input()
  public set animalsOnSelection(animals: { country_code?: string; identification_number?: string }[] | undefined) {
    if (!this._excludeKeys.size && animals?.length) {
      this._excludeKeys = buildAnimalSelectionKeySet(animals as never);
    }
  }

  public setQ(val: string): void {
    this._q.next(val);
  }

  public setFilterByColorValue(value: string | null): void {
    this.filterByColorValue$.next(value);
    setTimeout(() => this.stackedList?.scrollToTop(), 0);
  }

  public getMarkColorHex(value: string): string {
    return getSelectionMarkColorHex(value);
  }

  public getColorFilterTitle(c: SelectionMarkColor): string {
    return getMarkColorFilterTitle(c);
  }

  public getTotalCount(animals: RvoAnimal[] | null): number {
    return animals?.length ?? 0;
  }

  /** Aantal dieren dat in de geboortedatum-band van deze kleur valt. */
  public getCountForColor(animals: RvoAnimal[] | null, colorValue: string): number {
    if (!animals?.length || !this._selectionColors?.length) return 0;
    const band = this._selectionColors.find((c) => c.value === colorValue);
    if (!band || (!band.min_birth_date && !band.max_birth_date)) return 0;
    return animals.filter((a) =>
      matchesBirthDateRange(a.geboorteDatum, band.min_birth_date, band.max_birth_date)
    ).length;
  }

  public getUsedColors(colors: SelectionMarkColor[] | null): SelectionMarkColor[] {
    return (colors || []).filter((c) => c.used !== false);
  }

  /** Voorgestelde kleur voor een dier (geboortedatum-band); voor kleurstreep in lijst. */
  public getSuggestedColor(animal: RvoAnimal): string | null {
    return suggestMarkColorByBirthDate(animal.geboorteDatum, this._selectionColors);
  }

  public addNewAnimal(): void {
    from(
      this._modalCtrl.create({
        component: AnimalSelectionNewAnimalDialogComponent,
        componentProps: { selectionId: this.selection?.id }
      })
    )
      .pipe(
        tap((modal) => modal.present()),
        switchMap((modal) => modal.onDidDismiss<AnimalSelectionAddAnimal>()),
        filter((res) => !!res?.data),
        map(({ data }) => data)
      )
      .subscribe({
        next: (val: AnimalSelectionAddAnimal) => this._modalCtrl.dismiss(val)
      });
  }

  private _askForAge = (message: string): Observable<boolean> =>
    from(
      this._alertCtrl.create({
        header: 'Let op',
        message,
        buttons: [
          { text: 'Annuleren', role: 'cancel' },
          { text: 'Dier toevoegen', role: 'confirm' }
        ]
      })
    ).pipe(
      tap((modal) => modal.present()),
      switchMap((modal) => modal.onDidDismiss()),
      map((res) => res.role === 'confirm')
    );

  public selectAnimal(animal: RvoAnimal): void {
    const { min_birth_date, max_birth_date } = this.selection ?? {};
    if (isAfterBirthDateRange(animal.geboorteDatum, min_birth_date, max_birth_date)) {
      this._askForAge('Dit dier is te jong. Wilt u het alsnog toevoegen?')
        .pipe(take(1), tap((res) => res && this.dismissModal(animal)))
        .subscribe();
    } else if (isBeforeBirthDateRange(animal.geboorteDatum, min_birth_date, max_birth_date)) {
      this._askForAge('Dit dier is te oud. Wilt u het alsnog toevoegen?')
        .pipe(take(1), tap((res) => res && this.dismissModal(animal)))
        .subscribe();
    } else {
      this.dismissModal(animal);
    }
  }

  private _isBirthDateInSelectionRange(geboorteDatum: string | undefined): boolean {
    const { min_birth_date, max_birth_date } = this.selection ?? {};
    if (!min_birth_date && !max_birth_date) {
      return true;
    }

    return matchesBirthDateRange(geboorteDatum, min_birth_date, max_birth_date);
  }

  private dismissModal = (animal: RvoAnimal): void => {
    const suggestedColor =
      (animal as EnrichedRvoAnimal).suggestedColorValue
      ?? suggestMarkColorByBirthDate(animal.geboorteDatum, this._selectionColors);
    const payload: AnimalSelectionAddAnimal = {
      country_code: animal.dierLandcode,
      identification_number: animal.dierLevensnummer,
      selection_mark_color: suggestedColor
    };
    if (!animalSelectionUsesRvoPool(this.selection ?? {})) {
      if (animal.dierWerknummer) payload.work_number = animal.dierWerknummer;
      if (animal.dierHaarkleur) payload.haarkleur = animal.dierHaarkleur;
      if (animal.geboorteDatum) {
        payload.date_of_birth = dayjs(animal.geboorteDatum, 'DD-MM-YYYY').format('YYYY-MM-DD');
      }
      if (animal.datumLaatsteMutatieDierkenmerken) {
        payload.date_of_birth_mutation = dayjs(animal.datumLaatsteMutatieDierkenmerken, 'DD-MM-YYYY').format('YYYY-MM-DD');
      }
    }
    setTimeout(() => this._modalCtrl.dismiss(payload));
  };

  private _getAnimals = (): Observable<RvoAnimal[]> => {
    const key = this._storageKey();

    if (this._poolCache.hasCachedAnimals(key)) {
      return of(this._poolCache.getParsedAnimals(key));
    }

    this.loading = true;

    const source$ = !animalSelectionUsesRvoPool(this.selection ?? {})
      ? this._animalSelectionService.getExcelAnimals(this.selection.id)
      : this._stableService.getRvoAnimals(this.stableId, this.selection?.species ?? undefined);

    return source$.pipe(
      tap((animals: RvoAnimal[]) => {
        this._poolCache.setParsedAnimals(key, animals);
        this._rvoAnimals$.next(animals);
        const enrichedCached = this._poolCache.getEnrichedList(key);
        if (enrichedCached && enrichedCached.length === animals.length) {
          this._enrichedAnimals$.next(this._excludeOnSelection(enrichedCached));
        } else {
          this._startEnrichPipeline(animals, key);
        }
      }),
      catchError(() => of([] as RvoAnimal[])),
      finalize(() => {
        this.loading = false;
      })
    );
  };

  public forceRefreshList = (): void => {
    const key = this._storageKey();
    this._poolCache.clear(key);
    this._enrichedAnimals$.next([]);
    this.loading = true;
    this._getAnimals().pipe(take(1)).subscribe();
  };

  private _startEnrichPipeline(animals: RvoAnimal[], storageKey: string): void {
    this._enrichSubscription?.unsubscribe();
    this.processing = true;

    this._enrichSubscription = combineLatest([of(animals), this._selectionColors$]).pipe(
      take(1),
      switchMap(([list, colors]) =>
        this._poolCache.enrichAnimalsChunked(storageKey, list, colors, {
          ageInDays: (dob) => this._a(dob),
          suggestColor: (dob, cols) => suggestMarkColorByBirthDate(dob, cols)
        })
      )
    ).subscribe({
      next: (enriched) => {
        this._enrichedAnimals$.next(this._excludeOnSelection(enriched));
      },
      complete: () => {
        this.processing = false;
      },
      error: () => {
        this.processing = false;
      }
    });
  }

  private _excludeOnSelection(animals: EnrichedRvoAnimal[]): EnrichedRvoAnimal[] {
    if (!this._excludeKeys.size) {
      return animals;
    }
    return animals.filter((a) => !this._excludeKeys.has(rvoAnimalKey(a)));
  }

  private _applySearchFilter(animals: EnrichedRvoAnimal[], q: string): EnrichedRvoAnimal[] {
    if (!q) {
      return animals;
    }
    return animals.filter((animal) => animal.dierWerknummer?.indexOf(q) !== -1);
  }

  private _applyListFilters(
    animals: EnrichedRvoAnimal[],
    q: string,
    colorValue: string | null,
    selectionColors: SelectionMarkColor[]
  ): EnrichedRvoAnimal[] {
    let result = animals;
    if (q) {
      result = result.filter((animal) => animal.dierWerknummer?.indexOf(q) !== -1);
    } else if (colorValue && selectionColors?.length) {
      result = result.filter((animal) => animal.suggestedColorValue === colorValue);
    }
    return result;
  }

  private _a = (val: string): number => {
    const correctBirthDay: string = dayjs(val, 'DD-MM-YYYY').format();
    return this._agePipe.transform(correctBirthDay, { base: 1 });
  };

  private _ageOf(animal: RvoAnimal): number {
    const en = animal as EnrichedRvoAnimal;
    return en.ageInDays != null ? en.ageInDays : this._a(animal.geboorteDatum);
  }

  private _countsByColorFromEnriched(
    animals: EnrichedRvoAnimal[],
    colors: SelectionMarkColor[]
  ): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const c of colors) {
      counts[c.value] = 0;
    }
    for (const a of animals) {
      if (a.suggestedColorValue != null && Object.hasOwn(counts, a.suggestedColorValue)) {
        counts[a.suggestedColorValue]++;
      }
    }
    return counts;
  }
}
